import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
    ShieldCheck, ShieldAlert, X, RefreshCw, Copy, Check, AlertTriangle, Bell, Smartphone,
    Battery, MapPin, Layers, Maximize, Power, ChevronRight,
} from 'lucide-react';
import { PermissionsService, type FullPermissionStatus } from '../services/PermissionsService';
import { useAuth } from '../context/AuthContext';

/**
 * "Estado de permisos": muestra en vivo cada permiso que las alarmas y alertas
 * necesitan (notificaciones, pantalla completa de Android 14, sobre otras
 * apps, batería, ubicación, autoinicio del fabricante), con un botón para
 * arreglar cada uno y los datos del teléfono. Sirve para saber al tiro por qué
 * algo no suena en OTRO celular: quien prueba manda una captura o el texto
 * copiado, en vez de adivinar.
 *
 * También vigila solo: si falta algo crítico (notificaciones apagadas, o —para
 * Premium— pantalla completa / sobre otras apps), muestra un aviso arriba que
 * abre esta pantalla. El asistente inicial de permisos solo corre una vez;
 * esto cubre lo que pasa después (Android quita permisos a apps sin uso, el
 * usuario los revoca, o un permiso nuevo como el de Android 14).
 *
 * Se abre desde: el aviso automático, Mi perfil → "Estado de permisos", o
 * cualquier lugar con `openPermissionsPanel()`.
 */
export const openPermissionsPanel = () => window.dispatchEvent(new CustomEvent('open-permissions-panel'));

type Row = {
    id: string;
    icon: React.ElementType;
    title: string;
    desc: string;
    ok: boolean | null;          // null = Android no deja verificarlo
    critical: boolean;
    actionLabel: string;
    action: () => Promise<void>;
};

// Pantalla completa y "sobre otras apps" cuentan como críticos para TODOS, no solo
// Premium: las alertas de "escanearon a tu mascota", geocerca y SOS se abren a
// pantalla completa para cualquier plan (solo la alarma de dosis es Premium).
const isCriticalMissing = (s: FullPermissionStatus) =>
    !s.notifications || s.doseChannelBlocked || (s.sdk >= 34 && !s.fullScreen) || !s.overlay;

export const PermissionsWatcher: React.FC = () => {
    const { user } = useAuth();
    const isPremium = (user as any)?.plan === 'premium' || (user as any)?.plan === 'family';
    const isNative = Capacitor.isNativePlatform();

    const [status, setStatus] = useState<FullPermissionStatus | null>(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [checking, setChecking] = useState(false);
    const [copied, setCopied] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem('perm_banner_dismissed') === '1'; } catch { return false; }
    });

    const refresh = useCallback(async () => {
        if (!isNative) return;
        setChecking(true);
        const s = await PermissionsService.getFullStatus();
        if (s) setStatus(s);
        setChecking(false);
    }, [isNative]);

    // Al abrir, al volver a la app (de los ajustes del sistema) y cada 30 s.
    useEffect(() => {
        if (!isNative || !user) return;
        refresh();
        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        const t = setInterval(refresh, 30000);
        return () => { document.removeEventListener('visibilitychange', onVisible); clearInterval(t); };
    }, [isNative, user, refresh]);

    useEffect(() => {
        const open = () => { setPanelOpen(true); refresh(); };
        window.addEventListener('open-permissions-panel', open);
        return () => window.removeEventListener('open-permissions-panel', open);
    }, [refresh]);

    const rows: Row[] = useMemo(() => {
        if (!status) return [];
        const s = status;
        const list: Row[] = [
            {
                id: 'notifications', icon: Bell, title: 'Notificaciones',
                desc: !s.notifications
                    ? 'Están apagadas: no llegarán recordatorios de dosis ni alertas SOS.'
                    : s.doseChannelBlocked
                        ? 'El canal "Alertas de dosis" está bloqueado en los ajustes del teléfono.'
                        : 'Recordatorios de dosis, alertas SOS y avisos.',
                ok: s.notifications && !s.doseChannelBlocked, critical: true,
                actionLabel: 'Activar',
                action: () => (!s.notifications ? PermissionsService.fixNotifications() : PermissionsService.openNotificationSettings()),
            },
        ];
        if (s.sdk >= 34) {
            list.push({
                id: 'fullscreen', icon: Maximize, title: 'Alerta a pantalla completa',
                desc: 'Android 14+ la trae apagada: sin ella las alertas (escanearon a tu mascota, salió de la zona segura, SOS, dosis) llegan como una notificación común en vez de abrirse sobre la pantalla bloqueada.',
                ok: s.fullScreen, critical: true, actionLabel: 'Activar',
                action: () => PermissionsService.openFullScreenIntentSettings(),
            });
        }
        list.push(
            {
                id: 'overlay', icon: Layers, title: 'Mostrar sobre otras apps',
                desc: 'Permite que las alertas SOS, de geocerca, de escaneo del QR y de dosis se abran aunque estés usando otra aplicación.',
                ok: s.overlay, critical: true, actionLabel: 'Activar',
                action: () => PermissionsService.requestOverlayPermissionIntent(),
            },
            {
                id: 'battery', icon: Battery, title: 'Batería sin restricciones',
                desc: 'Evita que el teléfono "duerma" la app y se salte alarmas con la pantalla apagada.',
                ok: s.battery, critical: false, actionLabel: 'Activar',
                action: () => PermissionsService.requestBatteryOptimizationPermission(),
            },
            {
                id: 'location', icon: MapPin, title: 'Ubicación',
                desc: s.location && !s.locationPrecise
                    ? 'Solo ubicación aproximada — el GPS de tu mascota funciona mejor con la precisa.'
                    : 'Mapa, veterinarias cercanas, rastreo GPS y alertas de mascotas perdidas en tu zona.',
                ok: s.location, critical: false, actionLabel: 'Activar',
                action: () => PermissionsService.fixLocation(),
            },
        );
        if (s.oemKnown) {
            list.push({
                id: 'autostart', icon: Power, title: `Autoinicio (${s.manufacturer})`,
                desc: `Ese fabricante tiene una lista propia de apps permitidas en segundo plano. Android no deja comprobarla desde la app: ábrela y asegúrate de que Amigo Peludo esté activada. Es la causa más común de alarmas que no suenan en este tipo de teléfono.`,
                ok: null, critical: false, actionLabel: 'Abrir ajustes',
                action: () => PermissionsService.openAutostartSettings(),
            });
        }
        return list;
    }, [status, isPremium]);

    const missingCritical = rows.filter(r => r.ok === false && r.critical);
    const showBanner = isNative && !!user && !!status && !dismissed && !panelOpen
        && !!localStorage.getItem('permissions_handled') && isCriticalMissing(status);

    const copyDiagnostic = async () => {
        if (!status) return;
        const line = (label: string, v: boolean | null) => `${label}: ${v === null ? 'no verificable' : v ? 'OK' : 'FALTA'}`;
        const text = [
            'Diagnóstico de permisos — Amigo Peludo',
            `Teléfono: ${status.manufacturer} ${status.model} · Android ${status.release} (API ${status.sdk})`,
            `Plan: ${isPremium ? 'Premium' : 'Gratis'}`,
            ...rows.map(r => line(r.title, r.ok)),
        ].join('\n');
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            alert(text);
        }
    };

    if (!isNative) return null;

    return (
        <>
            {showBanner && (
                <div className="fixed left-3 right-3 z-[900] top-[calc(env(safe-area-inset-top)+8px)]">
                    <div className="bg-amber-500 text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3">
                        <AlertTriangle size={20} className="shrink-0" />
                        <button onClick={() => { setPanelOpen(true); refresh(); }} className="flex-1 text-left min-w-0">
                            <p className="text-xs font-black leading-tight">Faltan permisos para que las alertas funcionen</p>
                            <p className="text-[10px] font-bold opacity-90 mt-0.5 flex items-center gap-1">Toca para revisarlos <ChevronRight size={11} /></p>
                        </button>
                        <button onClick={() => { setDismissed(true); try { sessionStorage.setItem('perm_banner_dismissed', '1'); } catch {} }}
                            className="p-1.5 rounded-full bg-white/20 shrink-0" aria-label="Cerrar aviso"><X size={14} /></button>
                    </div>
                </div>
            )}

            {panelOpen && (
                <div className="fixed inset-0 z-[5000] bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setPanelOpen(false)}>
                    <div className="bg-slate-50 w-full sm:max-w-md max-h-[92vh] rounded-t-[2rem] sm:rounded-[2rem] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="bg-[#1E3A8A] px-5 pt-5 pb-5 shrink-0">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2.5">
                                    {missingCritical.length === 0
                                        ? <ShieldCheck size={22} className="text-emerald-300" />
                                        : <ShieldAlert size={22} className="text-amber-300" />}
                                    <h3 className="text-lg font-black text-white uppercase tracking-tight">Estado de permisos</h3>
                                </div>
                                <button onClick={() => setPanelOpen(false)} className="text-white/70 hover:text-white p-1"><X size={20} /></button>
                            </div>
                            {status && (
                                <>
                                    <p className={`text-xs font-bold ${missingCritical.length === 0 ? 'text-emerald-200' : 'text-amber-200'}`}>
                                        {missingCritical.length === 0
                                            ? 'Todo lo importante está en orden.'
                                            : `Falta${missingCritical.length > 1 ? 'n' : ''} ${missingCritical.length} permiso${missingCritical.length > 1 ? 's' : ''} importante${missingCritical.length > 1 ? 's' : ''}.`}
                                    </p>
                                    <p className="text-[10px] font-bold text-blue-200 mt-1.5 flex items-center gap-1.5">
                                        <Smartphone size={11} /> {status.manufacturer} {status.model} · Android {status.release} (API {status.sdk})
                                    </p>
                                </>
                            )}
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                            {!status && <p className="text-center text-xs text-slate-400 font-bold py-8">Leyendo el estado del teléfono…</p>}
                            {rows.map(r => {
                                const Icon = r.icon;
                                const tone = r.ok === true ? 'emerald' : r.ok === null ? 'slate' : r.critical ? 'red' : 'amber';
                                const pill = r.ok === true ? 'Activo' : r.ok === null ? 'No verificable' : r.critical ? 'Falta' : 'Recomendado';
                                const pillCls = { emerald: 'bg-emerald-100 text-emerald-700', slate: 'bg-slate-200 text-slate-600', red: 'bg-red-100 text-red-700', amber: 'bg-amber-100 text-amber-700' }[tone];
                                const iconCls = { emerald: 'bg-emerald-50 text-emerald-500', slate: 'bg-slate-100 text-slate-500', red: 'bg-red-50 text-red-500', amber: 'bg-amber-50 text-amber-500' }[tone];
                                return (
                                    <div key={r.id} className={`bg-white rounded-2xl border p-4 ${r.ok === false && r.critical ? 'border-red-200' : 'border-slate-100'}`}>
                                        <div className="flex items-start gap-3">
                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${iconCls}`}><Icon size={17} /></div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-sm font-black text-slate-800 truncate">{r.title}</p>
                                                    <span className={`shrink-0 text-[9px] font-black uppercase px-2 py-0.5 rounded-full ${pillCls}`}>{pill}</span>
                                                </div>
                                                <p className="text-[11px] text-slate-500 font-medium leading-relaxed mt-1">{r.desc}</p>
                                                {r.ok !== true && (
                                                    <button onClick={async () => { await r.action(); setTimeout(refresh, 600); }}
                                                        className="mt-2.5 px-4 py-2 bg-[#1E3A8A] text-white rounded-xl text-[10px] font-black uppercase tracking-wide active:scale-95 transition-transform">
                                                        {r.actionLabel}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="p-4 bg-white border-t border-slate-100 flex gap-2 shrink-0">
                            <button onClick={refresh} disabled={checking}
                                className="flex-1 py-3 bg-slate-100 text-slate-600 rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 disabled:opacity-50">
                                <RefreshCw size={14} className={checking ? 'animate-spin' : ''} /> Volver a comprobar
                            </button>
                            <button onClick={copyDiagnostic} disabled={!status}
                                className="flex-1 py-3 bg-[#1E3A8A] text-white rounded-2xl font-black text-[11px] uppercase flex items-center justify-center gap-2 disabled:opacity-50">
                                {copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar diagnóstico</>}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PermissionsWatcher;
